import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Divider,
  Stack,
  Avatar,
} from '@mui/material';
import {
  Close,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  AccountBalance,
  Schedule,
  CheckCircle,
  Lock,
  DriveEta,
} from '@mui/icons-material';
import {
  driverEarningsService,
  DriverDetailedEarnings,
  DriverDetailedEarningsFilters,
} from '@/services/driverEarningsService';

interface DriverDetailedEarningsModalProps {
  open: boolean;
  onClose: () => void;
  driverId: string;
  driverName: string;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const DriverDetailedEarningsModal: React.FC<DriverDetailedEarningsModalProps> = ({
  open,
  onClose,
  driverId,
  driverName,
}) => {
  const [earnings, setEarnings] = useState<DriverDetailedEarnings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'all' | 'today' | 'week' | 'month' | 'year'>('all');

  useEffect(() => {
    if (open) {
      fetchEarnings();
    }
  }, [open, driverId, period]);

  const fetchEarnings = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: DriverDetailedEarningsFilters = {
        period: period !== 'all' ? period : undefined,
      };

      const response = await driverEarningsService.getDriverDetailedEarnings(driverId, filters);
      setEarnings(response.data);
    } catch (err: any) {
      console.error('Error fetching driver detailed earnings:', err);
      setError(err.response?.data?.message || 'Failed to load detailed earnings');
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (event: any) => {
    setPeriod(event.target.value);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <DriveEta />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {driverName} - Earnings Breakdown
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Detailed earnings analysis and transaction history
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Period Filter */}
        <Box mb={3}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Period</InputLabel>
            <Select value={period} onChange={handlePeriodChange} label="Period">
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="week">This Week</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="year">This Year</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : earnings ? (
          <Box>
            {/* Driver Info & Summary Cards */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} md={3}>
                <Card sx={{ bgcolor: 'success.lighter', borderLeft: 4, borderColor: 'success.main' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <AccountBalance fontSize="small" sx={{ color: 'success.main' }} />
                      <Typography variant="caption" color="text.secondary">
                        Available Balance
                      </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {formatCurrency(earnings.availableBalance)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card sx={{ bgcolor: 'warning.lighter', borderLeft: 4, borderColor: 'warning.main' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Lock fontSize="small" sx={{ color: 'warning.main' }} />
                      <Typography variant="caption" color="text.secondary">
                        Pending Earnings
                      </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {formatCurrency(earnings.pendingEarnings)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card sx={{ bgcolor: 'info.lighter', borderLeft: 4, borderColor: 'info.main' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <AttachMoney fontSize="small" sx={{ color: 'info.main' }} />
                      <Typography variant="caption" color="text.secondary">
                        Pending Withdrawals
                      </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {formatCurrency(earnings.pendingWithdrawals)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card sx={{ bgcolor: 'primary.lighter', borderLeft: 4, borderColor: 'primary.main' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <CheckCircle fontSize="small" sx={{ color: 'primary.main' }} />
                      <Typography variant="caption" color="text.secondary">
                        Total Withdrawn
                      </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {formatCurrency(earnings.totalWithdrawn)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Total Earnings Card */}
            <Card sx={{ mb: 3, bgcolor: 'success.lighter' }}>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <TrendingUp sx={{ color: 'success.main' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Total Earnings
                      </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main' }}>
                      {formatCurrency(earnings.totalEarnings)}
                    </Typography>
                  </Grid>
                  {earnings.releaseInfo?.nextReleaseAt && (
                    <Grid item xs={12} md={6}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Schedule sx={{ color: 'info.main' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Next Release
                        </Typography>
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.main' }}>
                        {formatCurrency(earnings.releaseInfo.nextReleaseAmount)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(earnings.releaseInfo.nextReleaseAt)} (
                        {earnings.releaseInfo.hoursUntilRelease} hours)
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Driver Contact Info */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Driver Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={1}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Name
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {earnings.driver.name}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {earnings.driver.email}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={1}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Phone
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {earnings.driver.phone}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Status
                        </Typography>
                        <Box>
                          <Chip
                            label={earnings.driver.status}
                            size="small"
                            color={
                              earnings.driver.status.toLowerCase() === 'active'
                                ? 'success'
                                : 'default'
                            }
                          />
                        </Box>
                      </Box>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Recent Bookings */}
            {earnings.recentBookings && earnings.recentBookings.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Recent Bookings
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell>Booking Number</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell>Release Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {earnings.recentBookings.map((booking) => (
                          <TableRow key={booking.bookingNumber} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {booking.bookingNumber}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color:
                                    booking.status === 'released' ? 'success.main' : 'warning.main',
                                }}
                              >
                                {formatCurrency(booking.amount)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={booking.status}
                                size="small"
                                color={booking.status === 'released' ? 'success' : 'warning'}
                                icon={
                                  booking.status === 'released' ? (
                                    <CheckCircle fontSize="small" />
                                  ) : (
                                    <Lock fontSize="small" />
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{formatDate(booking.releaseDate)}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}
          </Box>
        ) : (
          <Alert severity="info">No earnings data available for this driver.</Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
